import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatForDatetimeLocalInput, parseFromDatetimeLocalInput, getOrganizationTimezone, getTimezoneDisplayName } from '../../lib/timezoneUtils';
import {
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  Box,
  FormControl,
  FormLabel,
  Select,
  Option,
  Input,
  Textarea,
  Button,
  Stack,
  RadioGroup,
  Radio,
  FormHelperText,
  Alert,
} from '@mui/joy';
import { Sparkle as AutoAwesomeIcon } from '@phosphor-icons/react';
import { api } from '../../lib/api';
import { GBPPostPreview } from './GBPPostPreview';
import type { GBPPost } from '../../types/marketing';
import { localSEOError } from '../../lib/localSEOUtils';
import { GBPPostImageField } from './GBPPostImageField';
import type { MediaFile } from './mediaApi';

interface GBPProfile {
  id: number;
  location_name: string;
  profile_id: string;
  website?: string;
}

interface CreateGBPPostModalProps {
  open: boolean;
  onClose: () => void;
  profiles: GBPProfile[];
  initialProfileId?: number;
  onSubmit: (data: any) => Promise<void>;
}

export const CreateGBPPostModal: React.FC<CreateGBPPostModalProps> = ({
  open,
  onClose,
  profiles,
  initialProfileId,
  onSubmit
}) => {
  const initialProfile = profiles.find(profile => profile.id === initialProfileId) || profiles[0];
  const [formData, setFormData] = useState({
    google_business_profile: initialProfile?.id || 0,
    type: 'update',  // Changed from post_type to match backend model
    title: '',
    content: '',
    cta_type: 'learn_more',
    cta_link: initialProfile?.website || '',
    scheduled_time: null as Date | null,
    status: 'draft'
  });
  const [selectedMedia, setSelectedMedia] = useState<MediaFile | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [generatingAI, setGeneratingAI] = useState(false);
  const [aiGenerationError, setAiGenerationError] = useState('');

  const { data: orgData } = useQuery({
    queryKey: ['organization'],
    queryFn: async () => {
      const res = await api.get<{ time_zone?: string }>('/api/organizations/current/');
      return res.data;
    },
  });
  const orgTimezone = orgData?.time_zone || getOrganizationTimezone();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const submitData = {
        ...formData,
        scheduled_time: formData.scheduled_time?.toISOString() || null,
        // If we have selected media, pass its ID instead of the old media field
        media_id: selectedMedia ? selectedMedia.id : undefined,
        media: undefined  // Clear the old media field
      };
      await onSubmit(submitData);
      handleClose();
    } catch (err: any) {
      setError(localSEOError(err, 'Failed to create post'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      google_business_profile: initialProfile?.id || 0,
      type: 'update',  // Changed from post_type to match backend model
      title: '',
      content: '',
      cta_type: 'learn_more',
      cta_link: initialProfile?.website || '',
      scheduled_time: null,
      status: 'draft'
    });
    setSelectedMedia(null);
    setError('');
    setAiGenerationError('');
    onClose();
  };

  const titleMaxLength = 58;
  const contentMaxLength = 1500;

  const handleGenerateWithAI = async () => {
    // Check if we have a topic to generate from
    const topic = formData.title || prompt('What topic would you like to generate content about?');
    if (!topic) return;

    setGeneratingAI(true);
    setAiGenerationError('');

    try {
      const response = await api.post('/api/marketing/gbp-posts/generate/', {
        topic,
        post_type: formData.type,
        include_cta: true
      });

      const responseData = response.data as any;
      if (responseData.success && responseData.generated_content) {
        const content = responseData.generated_content;
        
        // Update form with generated content
        // Convert cta_type to lowercase since AI returns uppercase (CALL, LEARN_MORE)
        // but backend expects lowercase (call, learn_more)
        const aiCtaType = content.cta_type?.toLowerCase() || formData.cta_type;
        setFormData({
          ...formData,
          title: content.title || formData.title,
          content: content.content || formData.content,
          cta_type: aiCtaType,
          cta_link: aiCtaType === 'call' ? '' : formData.cta_link,
        });
      }
    } catch (err: any) {
      console.error('AI generation error:', err);
      setAiGenerationError(err.response?.data?.error || 'Failed to generate content with AI');
    } finally {
      setGeneratingAI(false);
    }
  };

  if (!profiles.length) return null;

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog 
        size="lg" 
        sx={{ 
          width: 'min(1080px, 94vw)',
          minWidth: 0,
          maxWidth: 1320,
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <ModalClose />
        <Typography level="h4">Create Google Business Post</Typography>
        
        <Box sx={{ 
          overflow: 'auto',
          flex: 1,
          minHeight: 0,
          mt: 2
        }}>
          <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
            {error && (
              <Alert color="danger">{error}</Alert>
            )}

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 360px' },
                gap: 3,
                alignItems: 'start',
              }}
            >
            {/* Left column — post content */}
            <Stack spacing={2}>
            <FormControl required>
              <FormLabel>Business Profile</FormLabel>
              <Select value={formData.google_business_profile} onChange={(_, value) => {
                const profile = profiles.find(item => item.id === value);
                if (profile) setFormData(previous => ({...previous, google_business_profile: profile.id, cta_link: previous.cta_type === 'call' ? '' : profile.website || ''}));
              }}>
                {profiles.map(profile => <Option key={profile.id} value={profile.id}>{profile.location_name}</Option>)}
              </Select>
            </FormControl>

            <FormControl required>
              <FormLabel>Post Type</FormLabel>
              <RadioGroup
                orientation="horizontal"
                sx={{ flexWrap: 'wrap', rowGap: 1 }}
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              >
                <Radio value="update" label="Update" />
                <Radio value="offer" label="Offer" />
                <Radio value="event" label="Event" />
                <Radio value="product" label="Product" />
              </RadioGroup>
            </FormControl>

            <FormControl required>
              <FormLabel>Title</FormLabel>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                slotProps={{
                  input: { maxLength: titleMaxLength }
                }}
              />
              <FormHelperText>
                {formData.title.length}/{titleMaxLength} characters
              </FormHelperText>
            </FormControl>

            <FormControl required>
              <FormLabel>Content</FormLabel>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                minRows={8}
                maxRows={16}
                slotProps={{
                  textarea: { maxLength: contentMaxLength }
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                <FormHelperText sx={{ m: 0 }}>
                  {formData.content.length}/{contentMaxLength} characters
                </FormHelperText>
                <Button
                  size="sm"
                  variant="soft"
                  color="primary"
                  startDecorator={<AutoAwesomeIcon />}
                  onClick={handleGenerateWithAI}
                  loading={generatingAI}
                  disabled={generatingAI}
                >
                  Generate with AI
                </Button>
              </Box>
              {aiGenerationError && (
                <Alert color="danger" size="sm" sx={{ mt: 1 }}>
                  {aiGenerationError}
                </Alert>
              )}
            </FormControl>

            </Stack>

            {/* Right column — image, schedule, status */}
            <Stack spacing={2}>
            <GBPPostImageField value={selectedMedia} onChange={setSelectedMedia} />

            <FormControl>
              <FormLabel>Call-to-Action Type</FormLabel>
              <Select
                value={formData.cta_type || ''}
                onChange={(e, value) => {
                  const newCtaType = (value || '') as string;
                  setFormData({
                    ...formData,
                    cta_type: newCtaType,
                    cta_link: newCtaType === 'call' ? '' : formData.cta_link,
                  });
                }}
              >
                <Option value="">No CTA</Option>
                <Option value="book">Book</Option>
                <Option value="order">Order Online</Option>
                <Option value="shop">Shop</Option>
                <Option value="learn_more">Learn More</Option>
                <Option value="sign_up">Sign Up</Option>
                <Option value="call">Call Now</Option>
              </Select>
            </FormControl>

            {formData.cta_type && formData.cta_type !== 'call' && (
              <FormControl>
                <FormLabel>CTA Link</FormLabel>
                <Input
                  type="url"
                  value={formData.cta_link}
                  onChange={(e) => setFormData({ ...formData, cta_link: e.target.value })}
                  placeholder="https://example.com"
                />
              </FormControl>
            )}

            <FormControl>
              <FormLabel>Schedule</FormLabel>
              <Input
                type="datetime-local"
                value={formData.scheduled_time ? formatForDatetimeLocalInput(new Date(formData.scheduled_time), orgTimezone) : ''}
                onChange={(e) => setFormData({
                  ...formData,
                  scheduled_time: e.target.value ? parseFromDatetimeLocalInput(e.target.value, orgTimezone) : null
                })}
                placeholder="Choose a scheduled time"
              />
              <FormHelperText>Times are in {getTimezoneDisplayName(orgTimezone)}. Scheduled posts require a future time; drafts stay unpublished.</FormHelperText>
            </FormControl>

            <FormControl required>
              <FormLabel>Status</FormLabel>
              <RadioGroup
                orientation="horizontal"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                sx={{ flexWrap: 'wrap', rowGap: 1 }}
              >
                <Radio value="draft" label="Draft" />
                <Radio value="pending_review" label="Pending Review" />
                <Radio value="scheduled" label="Scheduled" />
              </RadioGroup>
            </FormControl>
            </Stack>
            </Box>

            <GBPPostPreview post={formData as Partial<GBPPost>} imageUrl={selectedMedia?.file} profileName={profiles.find(profile => profile.id === formData.google_business_profile)?.location_name} />

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
              <Button variant="plain" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" loading={submitting}>
                Create Post
              </Button>
            </Box>
            </Stack>
          </form>
        </Box>
      </ModalDialog>
    </Modal>
  );
};